import React, { useLayoutEffect, useState } from 'react';
import { format, addDays } from 'date-fns';
import toast from 'react-hot-toast';
import { RotateCcw } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useReactivateUserAsset, useUpdateUserAsset } from '../hooks/useUserAssets';
import { UserAsset } from '../types';

const BE_MESSAGE_PREFIX = 'Failed to reactivate user asset: ';
export const REACTIVATE_REASON_MAX_LENGTH = 500; // khớp rule `reason` max:500 ở BE

export const isAssetExpired = (asset: UserAsset) =>
  !!asset.expires_at && new Date(asset.expires_at).getTime() < Date.now();

// Đối chiếu với message BE trả về khi reactivate (404/422, dạng "Failed to reactivate user asset: <lý do>").
// So theo cụm cụ thể, không so chữ "expired" chung chung vì "Token has expired" (401) cũng chứa nó.
// Lỗi 4xx khác (ví dụ lý do quá dài) hiển thị đúng message BE thay vì câu chung.
export const reactivateErrorMessage = (message: string = '', status?: number) => {
  if (message.includes('User asset has expired')) {
    return 'Tài sản đã quá hạn sử dụng. Hãy gia hạn ngày hết hạn trước khi kích hoạt lại.';
  }
  if (message.includes('Only revoked')) {
    return 'Tài sản không còn ở trạng thái "Bị thu hồi".';
  }
  if (message.includes('User asset not found')) {
    return 'Tài sản không tồn tại hoặc đã bị xoá.';
  }
  if (status === 404) {
    // 404 không mang message nghiệp vụ: route chưa có trên server (BE chưa deploy endpoint reactivate).
    return 'Máy chủ chưa hỗ trợ kích hoạt lại tài sản (404). Vui lòng liên hệ quản trị hệ thống.';
  }
  if (status && status >= 400 && status < 500 && message) {
    return message.replace(BE_MESSAGE_PREFIX, '');
  }
  return 'Có lỗi xảy ra khi kích hoạt lại tài sản. Vui lòng thử lại.';
};

interface ReactivateAssetDialogProps {
  asset: UserAsset | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReactivated?: (asset: UserAsset) => void;
}

/**
 * Kích hoạt lại 1 tài sản bị thu hồi.
 * - Còn hạn: xác nhận + lý do → POST /reactivate.
 * - Đã hết hạn: gia hạn expires_at (PUT có sẵn) trước, thành công mới POST /reactivate.
 * Dữ liệu hiển thị giữ nguyên khi đóng (chỉ tắt `open`) để không chớp nội dung lúc animation đóng.
 */
const ReactivateAssetDialog: React.FC<ReactivateAssetDialogProps> = ({ asset, open, onOpenChange, onReactivated }) => {
  const [reason, setReason] = useState('');
  const [renewDate, setRenewDate] = useState('');
  const [renewError, setRenewError] = useState('');

  const reactivateMutation = useReactivateUserAsset();
  const renewMutation = useUpdateUserAsset();
  const queryClient = useQueryClient();

  // Chế độ + form chỉ khởi tạo khi mở (hoặc đổi sang tài sản khác). Không phụ thuộc cả object
  // `asset`: ở trang chi tiết nó được refetch sau khi gia hạn, reset lúc đó sẽ đổi Dialog giữa chừng.
  const [isRenewMode, setIsRenewMode] = useState(false);
  const assetId = asset?.id;
  // useLayoutEffect (không phải useEffect): chạy trước khi trình duyệt vẽ, để khung hình đầu tiên
  // đã đúng dạng — tránh chớp popup xác nhận trước popup gia hạn, hoặc lộ lý do của lần mở trước.
  useLayoutEffect(() => {
    if (open && asset) {
      setIsRenewMode(isAssetExpired(asset));
      setReason('');
      setRenewDate('');
      setRenewError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, assetId]);

  const isBusy = reactivateMutation.isPending || renewMutation.isPending;
  const minRenewDate = format(addDays(new Date(), 1), 'yyyy-MM-dd');
  const title = asset?.book?.title || 'tài sản';
  const who = asset?.user?.name || 'người dùng';

  const reactivate = async (target: UserAsset, messages: { success: string; errorPrefix?: string }) => {
    try {
      await reactivateMutation.mutateAsync({ id: target.id, reason: reason.trim() || undefined });
      toast.success(messages.success);
      onReactivated?.({ ...target, status: 'active' });
      return true;
    } catch (error: any) {
      console.error('Failed to reactivate asset:', error);
      const status: number | undefined = error?.response?.status;
      // 401: interceptor của api-client đã chuyển về trang đăng nhập.
      if (status === 401) return false;
      const message: string = error?.response?.data?.message || '';
      if (message.includes('Only revoked')) {
        // Trạng thái trên màn hình đã cũ: tải lại để hiện trạng thái thật.
        queryClient.invalidateQueries({ queryKey: ['userAssets'] });
        queryClient.invalidateQueries({ queryKey: ['userAsset', target.id] });
      }
      toast.error(`${messages.errorPrefix ?? ''}${reactivateErrorMessage(message, status)}`);
      return false;
    }
  };

  const confirm = async () => {
    if (!asset || isBusy) return;
    await reactivate(asset, { success: `Đã kích hoạt lại "${title}" cho ${who}.` });
    onOpenChange(false);
  };

  const renewAndReactivate = async () => {
    if (!asset || isBusy) return;
    if (!renewDate) {
      setRenewError('Vui lòng chọn ngày hết hạn mới.');
      return;
    }
    // Giống form tạo lô mã: gửi "YYYY-MM-DD", BE (timezone UTC) lưu 00:00 UTC — cũng là mốc so ở đây.
    if (new Date(renewDate).getTime() <= Date.now()) {
      setRenewError('Ngày hết hạn mới phải sau thời điểm hiện tại.');
      return;
    }
    setRenewError('');

    // Bước 1: gia hạn bằng API update có sẵn. Thất bại thì dừng, không kích hoạt lại.
    try {
      const updated = await renewMutation.mutateAsync({ id: asset.id, data: { expires_at: renewDate } });
      // PUT trả 200 + data=false khi không tìm thấy tài sản.
      if (updated === false) {
        toast.error('Gia hạn thất bại: tài sản không tồn tại hoặc đã bị xoá.');
        return;
      }
      // useUpdateUserAsset không làm mới trang chi tiết; nếu bước 2 lỗi, trang vẫn phải hiện hạn mới.
      queryClient.invalidateQueries({ queryKey: ['userAsset', asset.id] });
    } catch (error: any) {
      console.error('Failed to renew asset:', error);
      if (error?.response?.status === 401) return;
      toast.error('Gia hạn thất bại. Vui lòng kiểm tra ngày hết hạn và thử lại.');
      return;
    }

    // Bước 2: đã gia hạn xong mới kích hoạt lại.
    const newExpiry = format(new Date(renewDate), 'dd/MM/yyyy');
    await reactivate(
      { ...asset, expires_at: renewDate },
      {
        success: `Đã gia hạn đến ${newExpiry} và kích hoạt lại "${title}" cho ${who}.`,
        errorPrefix: `Đã gia hạn đến ${newExpiry} nhưng kích hoạt lại thất bại: `,
      }
    );
    onOpenChange(false);
  };

  const reasonField = (
    <div>
      <label htmlFor="reactivate-reason" className="block text-sm font-medium text-gray-700 mb-1">
        Lý do kích hoạt lại (tùy chọn)
      </label>
      <Textarea
        id="reactivate-reason"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        maxLength={REACTIVATE_REASON_MAX_LENGTH}
        placeholder="Nhập lý do kích hoạt lại..."
        rows={3}
        disabled={isBusy}
      />
    </div>
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Không cho đóng khi request đang chạy.
        if (!next && isBusy) return;
        onOpenChange(next);
      }}
    >
      {/* Popup xác nhận theo bố cục popup "Xác nhận thu hồi tài sản" (w-96, p-5, căn giữa) */}
      <DialogContent className={isRenewMode ? 'sm:max-w-md' : 'max-w-sm gap-0 rounded-md p-5 sm:rounded-md'}>
        {isRenewMode ? (
          <>
            <DialogHeader>
              <DialogTitle>Gia hạn trước khi kích hoạt lại</DialogTitle>
              <DialogDescription>
                <strong>"{title}"</strong> của <strong>{who}</strong> đã hết hạn
                {asset?.expires_at && <> từ {format(new Date(asset.expires_at), 'dd/MM/yyyy HH:mm')}</>}.
                Chọn ngày hết hạn mới để kích hoạt lại.
              </DialogDescription>
            </DialogHeader>
            <div>
              <label htmlFor="renew-expires-at" className="block text-sm font-medium text-gray-700 mb-1">
                Ngày hết hạn mới <span className="text-red-500">*</span>
              </label>
              <Input
                id="renew-expires-at"
                type="date"
                value={renewDate}
                min={minRenewDate}
                onChange={(e) => {
                  setRenewDate(e.target.value);
                  setRenewError('');
                }}
                disabled={isBusy}
                className={renewError ? 'border-red-500' : ''}
              />
              {renewError && <p className="text-red-500 text-sm mt-1">{renewError}</p>}
            </div>
            {reasonField}
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isBusy}>
                Hủy
              </Button>
              <Button variant="primary" onClick={renewAndReactivate} isLoading={isBusy} disabled={isBusy}>
                {renewMutation.isPending
                  ? 'Đang gia hạn...'
                  : reactivateMutation.isPending
                    ? 'Đang kích hoạt...'
                    : 'Gia hạn và kích hoạt lại'}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <div className="mt-3 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
              <RotateCcw className="h-6 w-6 text-blue-600" />
            </div>
            <DialogHeader className="space-y-0 text-center sm:text-center">
              <DialogTitle className="mt-4 text-lg font-medium leading-normal tracking-normal text-gray-900">
                Xác nhận kích hoạt lại
              </DialogTitle>
              <DialogDescription className="mt-2 px-7 py-3 text-sm text-gray-500">
                Kích hoạt lại <strong>"{title}"</strong> cho <strong>{who}</strong>?
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4">
              <label htmlFor="reactivate-reason" className="block text-sm font-medium text-gray-700 mb-2">
                Lý do kích hoạt lại (tùy chọn)
              </label>
              <Textarea
                id="reactivate-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={REACTIVATE_REASON_MAX_LENGTH}
                placeholder="Nhập lý do kích hoạt lại..."
                rows={3}
                disabled={isBusy}
                className="min-h-0 border-gray-300 text-base focus-visible:ring-blue-500 focus-visible:ring-offset-0"
              />
            </div>
            <div className="px-4 py-3 mt-4">
              <Button variant="primary" onClick={confirm} isLoading={isBusy} disabled={isBusy} className="w-full text-base shadow-sm">
                {isBusy ? 'Đang kích hoạt...' : 'Xác nhận kích hoạt lại'}
              </Button>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isBusy} className="mt-3 w-full text-base text-gray-700 shadow-sm">
                Hủy
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReactivateAssetDialog;
